import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import { receiveError } from '../../store/actions/notificationAction';
import { setOrderAction } from '../../store/actions/orderAction';

import pickupOrderRemote from '../../api/pickupOrderRemote';
import cartRemote from '../../api/cartRemote';

import PrevNext from '../prevAndNext/PrevNext';
import ProcessBarContainer from '../processBar/ProcessBarContainer';
import PickupOrderContainer from './PickupOrderContainer';

import Gha from '../../models/Gha';
import Cart from '../../models/Cart';
import ApiResponse from '../../models/ApiResponse';
import ResponseError from '../../models/ResponseError';
import ManualEntryEdit from '../../models/ManualEntryEdit';
import PickupOrderModel from '../../models/PickupOrder';
import User, { UserRoleType } from '../../models/User';

import moment, { Moment } from 'moment';
import MomentUtils from "@date-io/moment";
import { PO_SCHEMA } from '../../utils/pickupOrder';
import cartStyles from '../cart/cart.module.css';

interface PickupOrderWrapperContainerProps {
  user: User;
  cart: Cart;
  receiveError: Function;
  setOrder: Function;
  userRole: UserRoleType;
  manualEntryEdit: ManualEntryEdit;
};

interface PickupOrderWrapperContainerState {
  hasGhaInfo: boolean;
  ghaName: string;
  lastPickupOrder: PickupOrderModel;
  hasPaidPickupOrder: boolean;
  ghaSubscribedPO: boolean;
  pickupDate: Moment;
  orderType: string;
};

class PickupOrderWrapperContainer extends Component<PickupOrderWrapperContainerProps, PickupOrderWrapperContainerState> {

  state: PickupOrderWrapperContainerState = {
    hasGhaInfo: false,
    ghaName: '',
    lastPickupOrder: PO_SCHEMA,
    hasPaidPickupOrder: false,
    ghaSubscribedPO: false,
    pickupDate: new MomentUtils().date(),
    orderType: 'import'
  };

  componentDidMount() {

    // Manual entry process
    const orderNumber: string = this.props.manualEntryEdit.orderNumber;

    if (this.props.user.role === UserRoleType.GHA && orderNumber) {

      this.loadPickupOrderByNumber(orderNumber);
    }

    // Regular PO process
    if (this.props.cart.items.length > 0) {
      
      this.loadCartGHA();
    }

    this.loadLastPickupOrder(this.state.orderType);
  }

  loadCartGHA = (): void => {

    cartRemote.getCartGha().then((resp: ApiResponse<Gha>) => {

      const gha: Gha | undefined = resp.result;
      const error: ResponseError | undefined = resp.error;

      if (gha) {
        this.setState({
          ghaSubscribedPO: gha.info.po_support,
          ghaName: gha.name
        });
      }

      if (error) {
        this.props.receiveError(error);
      }
    });
  }

  loadLastPickupOrder = (orderType: string): void => {

    pickupOrderRemote.getLastPickupOrder(orderType).then(this.handleCommonOrderResponse);
  }

  loadPickupOrderByNumber = (orderNumber: string): void => {

    pickupOrderRemote.getPickupOrder(orderNumber).then(this.handleCommonOrderResponse);
  }

  handleCommonOrderResponse = (resp: ApiResponse<PickupOrderModel>): void => {

    const po: PickupOrderModel | undefined = resp.result;
    const error: ResponseError | undefined = resp.error;

    if (po) {
      po.maskedDriverId = po.driver_id ? po.driver_id.replace(/.(?=.{4,}$)/g, '#') : po.driver_id;
      this.setState({
        lastPickupOrder: po,
        hasPaidPickupOrder: true,
        pickupDate: moment(po.pickup_date)
      });

      this.props.setOrder(po);
    }

    if (error) {
      this.props.receiveError(error);
    }
  }

  renderPoOrMessage = (): JSX.Element => {

    const cartItems: number = this.props.cart.items.length;
    const cartTotal: number = this.props.cart.remainDue;
    let doNotShowPO: boolean = false;
    let renderContent: JSX.Element = <span></span>;

    /**
    * If user has AWB in Cart and its GHA does not have PO support feature configured, then display message
    * “Pick Up Order generation is not supported currently for <GHA name>.
    */
    if (cartItems > 0 && !this.state.hasPaidPickupOrder && !this.state.ghaSubscribedPO) {
      renderContent = <div className={ cartStyles.emptyCart }>Pick Up Order generation is not supported currently for { this.state.ghaName }!</div>;
      doNotShowPO = true;
    }

    /**
    * If user has clicked on this icon without having any AWB in Cart, then display message
    * “To generate Pick Up Order, first add AWBs to Cart and pay the due charges.”
    */
    if (!this.state.hasPaidPickupOrder) {
      renderContent = <div className={ cartStyles.emptyCart }>To generate Pick Up Order, first add AWB to Cart and proceed to “Payment” step.</div>;
      doNotShowPO = true;
    }

    /**
    * If user has AWBs in Cart, and GHA has PO-support and  there is due amount not paid yet, then display
    * “To generate Pick Up Order, first pay the due charges.”
    */
    if (cartItems > 0 && cartTotal > 0) {
      renderContent = <div className={ cartStyles.emptyCart }>To generate Pick Up Order, first pay the due charges!</div>;
      doNotShowPO = true;
    }

    if (doNotShowPO) {
      const prevButtonHref: string = this.props.userRole === UserRoleType.FORWARDER ? '/pay' : '/manual-entry-pay';
    
      return <Fragment>
              { renderContent }
              <PrevNext prevButton={ { active: true, href: `${prevButtonHref}`, animate: true } } />
            </Fragment>;
    }

    return <PickupOrderContainer
              pickupOrder={ this.state.lastPickupOrder }
              showDetails={ false }
              pickupDate={ this.state.pickupDate }
              showLine={ false }
              userRole={ this.props.userRole }
            />;
  }

  render() {
    
    return (
      <Fragment>
        <ProcessBarContainer type='import' userRole={ this.props.userRole }/>
        { this.renderPoOrMessage() }
      </Fragment>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    cart: state.cart,
    manualEntryEdit: state.manualEntryEdit
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    receiveError: (error: ResponseError) => dispatch(receiveError(error)),
    setOrder: (order: PickupOrderModel) => dispatch(setOrderAction(order))
  };
};
  
export default connect(mapStateToProps, mapDispatchToProps)(PickupOrderWrapperContainer);